// Fix for Radix UI Popover visibility issues
let observer: MutationObserver | null = null;
let styleEl: HTMLStyleElement | null = null;
let initialized = false;

export function initRadixPopoverFix() {
  // Prevent duplicate initialization
  if (initialized) {
    return;
  }
  initialized = true;

  // Add global styles to ensure Tailwind animation classes work
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.setAttribute('data-radix-popover-fix', 'true');
    styleEl.textContent = `
      /* Radix UI animation fixes */
      @keyframes enter {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      
      @keyframes exit {
        from {
          opacity: 1;
          transform: scale(1);
        }
        to {
          opacity: 0;
          transform: scale(0.95);
        }
      }
      
      /* Apply animations */
      [data-state="open"] {
        animation: enter 200ms ease-out;
      }
      
      [data-state="closed"] {
        animation: exit 200ms ease-in;
      }
      
      /* Ensure transform-origin works */
      [data-slot="popover-content"],
      [data-slot="dropdown-menu-content"] {
        transform-origin: var(--radix-popper-transform-origin);
      }
    `;
    document.head.appendChild(styleEl);
  }
  
  // Function to fix popover positioning
  const fixPopoverPosition = (wrapper: HTMLElement) => {
    const style = wrapper.getAttribute('style') || '';
    
    // If it's positioned way off-screen, reset it
    if (style.includes('translate(0px, -200%)') || style.includes('translate(-200%') || style.includes('translate(200%')) {
      // Find the trigger element
      const trigger = document.querySelector('[data-state="open"][data-slot="popover-trigger"], [data-state="open"][data-slot="dropdown-menu-trigger"]');
      
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        
        // Calculate position relative to trigger
        let left = rect.left;
        let top = rect.bottom + 8; // 8px gap
        
        // Ensure it stays within viewport
        if (left + wrapperRect.width > window.innerWidth) {
          left = window.innerWidth - wrapperRect.width - 10;
        }
        if (top + wrapperRect.height > window.innerHeight) {
          top = rect.top - wrapperRect.height - 8;
        }
        
        wrapper.style.transform = `translate(${left}px, ${top}px)`;
      } else {
        // Fallback: center it on screen
        wrapper.style.transform = `translate(${window.innerWidth / 2 - 150}px, ${window.innerHeight / 2 - 100}px)`;
      }
    }
  };
  
  // Clean up previous observer if it exists
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  // Watch for popovers being added to fix any CSS issues
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          // Check for Radix popper wrapper
          const wrapper = node.querySelector?.('[data-radix-popper-content-wrapper]') || 
                          (node.matches?.('[data-radix-popper-content-wrapper]') ? node : null);
          
          if (wrapper instanceof HTMLElement) {
            // Fix positioning
            setTimeout(() => fixPopoverPosition(wrapper), 0);
          }
          
          // Check for popover content
          const popoverContent = node.querySelector?.('[data-slot="popover-content"], [data-slot="dropdown-menu-content"]') || 
                                (node.matches?.('[data-slot="popover-content"], [data-slot="dropdown-menu-content"]') ? node : null);
          
          if (popoverContent instanceof HTMLElement) {
            // Ensure CSS variables are applied
            const computedStyle = getComputedStyle(popoverContent);
            if (!computedStyle.backgroundColor || computedStyle.backgroundColor === 'rgba(0, 0, 0, 0)') {
              // Apply fallback styles if CSS variables aren't working
              popoverContent.style.backgroundColor = 'white';
              popoverContent.style.color = 'black';
              popoverContent.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
            }
          }
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
  
  // Also fix any existing popovers
  document.querySelectorAll('[data-radix-popper-content-wrapper]').forEach((el) => {
    if (el instanceof HTMLElement) {
      fixPopoverPosition(el);
    }
  });
}

// Cleanup function for Turbolinks navigation
export function cleanupRadixPopoverFix() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  initialized = false;
}

// Reinitialize function for ensuring proper setup after navigation
export function reinitializeRadixPopoverFix() {
  cleanupRadixPopoverFix();
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    initRadixPopoverFix();
    // Additional fix for popover triggers after Turbolinks navigation
    fixPopoverTriggers();
  }, 50);
}

// Fix popover trigger buttons that may not have event handlers attached
function fixPopoverTriggers() {
  // Find all popover trigger buttons
  const triggers = document.querySelectorAll('[data-popover-trigger="true"], [data-state][role="button"]');
  triggers.forEach((trigger) => {
    if (trigger instanceof HTMLElement) {
      // Force re-attachment of event listeners by triggering a small DOM update
      const display = trigger.style.display;
      trigger.style.display = 'none';
      trigger.offsetHeight; // Force reflow
      trigger.style.display = display;
    }
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRadixPopoverFix);
} else {
  initRadixPopoverFix();
}

// Handle Turbolinks navigation
document.addEventListener('turbolinks:load', () => {
  reinitializeRadixPopoverFix();
});

// Clean up before cache to prevent stale observers
document.addEventListener('turbolinks:before-cache', () => {
  cleanupRadixPopoverFix();
});